import { useState } from "react";
import type { OccurrenceChatMessage } from "@edu/shared";
import { ApiError } from "../../lib/api.ts";
import { agoLabel } from "../../lib/when.ts";
import { TrashIcon } from "../shell/icons.tsx";
import { CHAT_REMOVE_LABEL, chatErrorText, chatMessageText } from "./chat-text.ts";

/**
 * Одно сообщение чата занятия. Общее на все три места показа.
 *
 * ⚠️ **Пузырь тот же, что в переписке кабинета (`.msg`), и это не экономия
 * правил**: сообщение в продукте выглядит одинаково везде, где его показывают, а
 * второй вид пузыря читался бы как другая сущность.
 *
 * ⚠️ **Имя автора приезжает готовым полем.** Собрал его сервер ПО ЧИТАТЕЛЮ, и
 * своей ветки выбора имени здесь нет ни одной: у вышедшего из группы приезжает
 * нейтральное «Ученик», потому что задать ему имя преподаватель уже не может, а
 * подставить полное было бы раскрытием, которому он не в силах помешать.
 *
 * ⚠️ **Переносы строк не оживают, и это осознанно:** поле чата однострочное
 * (раздел 10 проекта), то есть перевода строки в нём взяться неоткуда.
 * Вставленный из буфера текст показывается одной строкой, а `overflow-wrap` у
 * `.msg__line` не даёт длинному слову разорвать ленту.
 */
export function ChatMessage(props: {
  msg: OccurrenceChatMessage;
  /** Одно «сейчас» на всю отрисовку: иначе на границе полуночи ярлыки разойдутся. */
  now: number;
  /**
   * Убрать сообщение, либо `null`, если убирать нельзя.
   *
   * ⚠️ **В КОМНАТЕ он всегда `null`, и это решение, а не пропуск.** Удаление
   * живёт там, где переписку читают задним числом (карточка занятия); в комнате
   * её открывают на минуту, а раздел 16 проекта прямо говорит, что во время
   * занятия преподаватель в чат не смотрит по построению. Ручка удаления при
   * этом жива, и надгробие убранного из другого места приезжает в комнату
   * обычной страницей.
   */
  onRemove: ((messageId: string) => Promise<void>) | null;
}) {
  const { msg } = props;
  const shown = chatMessageText(msg);
  const when = agoLabel(msg.createdAt, props.now);
  /**
   * ⚠️ Кто что убирает, решил сервер: преподаватель любое сообщение своего
   * занятия, ученик только своё. Здесь кнопка прячется ровно там, где ручка
   * ответила бы отказом (кнопка, ведущая в отказ, хуже отсутствующей), а само
   * правило живёт предикатом в `WHERE`. Надгробие второй раз не убирается,
   * поэтому у него кнопки нет.
   */
  const removable = props.onRemove !== null && !shown.gone;
  const [asking, setAsking] = useState(false);

  return (
    <li className="msg" data-mine={msg.mine} data-dim={shown.dim}>
      <div className="msg__head">
        {/* Своё подписано «Вы», а не именем: подпись нужна читалке, которой
            выравнивание вправо не видно, а собственное имя человеку под его же
            словами не говорит ничего */}
        <span className="msg__who">{msg.mine ? "Вы" : msg.authorName}</span>
        <span className="msg__when" title={when.title}>
          {when.text}
        </span>
        {/*
          Значок «убрать» виден всегда, а не по наведению: наведения нет ни на
          телефоне, ни на планшете, то есть спрятанное за ним действие там не
          достать ничем. Пока задан вопрос, значка нет: два входа в одно и то же
          действие на одном пузыре читаются как два разных
        */}
        {removable && !asking && (
          <button
            type="button"
            className="btn btn--ico btn--danger msg__del"
            title={CHAT_REMOVE_LABEL}
            aria-label={CHAT_REMOVE_LABEL}
            onClick={() => setAsking(true)}
          >
            <TrashIcon />
          </button>
        )}
      </div>
      <p className={shown.dim ? "msg__line muted" : "msg__line"}>{shown.text}</p>
      {removable && asking && (
        <ConfirmRemove
          onRemove={async () => {
            await props.onRemove?.(msg.id);
          }}
          onCancel={() => setAsking(false)}
        />
      )}
    </li>
  );
}

/**
 * Второй шаг удаления: вопрос словами и сам вызов ручки.
 *
 * Двумя нажатиями и заливкой на втором: отменить это нельзя, а вес кнопки
 * сообщает о необратимости (`is-confirm`). ⚠️ Приём ровно тот же, что у
 * переписки кабинета и у карточки занятия: значок предлагает, слова с заливкой
 * подтверждают. Окна поверх экрана здесь нет и не заводится, третьего приёма на
 * один и тот же вопрос в продукте быть не должно.
 */
function ConfirmRemove(props: { onRemove: () => Promise<void>; onCancel: () => void }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const run = () => {
    void (async () => {
      setBusy(true);
      setErr(null);
      try {
        await props.onRemove();
        // Строку убирает не этот компонент, а следующая страница: надгробие
        // собирает сервер, и второго места его сборки в проекте нет
      } catch (e) {
        setErr(
          e instanceof ApiError
            ? chatErrorText(e.status, e.code, e.message)
            : e instanceof Error
              ? e.message
              : String(e),
        );
      } finally {
        setBusy(false);
      }
    })();
  };

  return (
    <>
      {err !== null && <p className="err">{err}</p>}
      <div className="msg__actions">
        <button type="button" className="btn btn--danger is-confirm" disabled={busy} onClick={run}>
          Да, убрать
        </button>
        <button type="button" className="btn btn--quiet" disabled={busy} onClick={props.onCancel}>
          Отмена
        </button>
      </div>
    </>
  );
}
